/** Work Samples page (/work-samples) capability helpers. */

export const WORK_SAMPLES_VIEW_SCOPES = Object.freeze({
  LINKED_STUDENTS: 'linked_students',
  ORGANIZATION: 'organization',
  VENDOR: 'vendor',
  ALL: 'all'
});

export { FORM_RELATIONSHIP_TYPES, WORK_SAMPLE_FORM_ASSIGNMENT_TYPE } from '../constants/formRelationships';

function isConfigFlagTrue(value) {
  return value === true;
}

export function normalizeWorkSamplesViewScope(value) {
  const v = String(value || '')
    .trim()
    .toLowerCase();
  if (v === WORK_SAMPLES_VIEW_SCOPES.ORGANIZATION || v === 'org') {
    return WORK_SAMPLES_VIEW_SCOPES.ORGANIZATION;
  }
  if (v === WORK_SAMPLES_VIEW_SCOPES.VENDOR) {
    return WORK_SAMPLES_VIEW_SCOPES.VENDOR;
  }
  if (v === WORK_SAMPLES_VIEW_SCOPES.ALL) {
    return WORK_SAMPLES_VIEW_SCOPES.ALL;
  }
  return WORK_SAMPLES_VIEW_SCOPES.LINKED_STUDENTS;
}

export function getWorkSamplesPageCapabilities(configuration) {
  const cfg = configuration && typeof configuration === 'object' ? configuration : {};
  return {
    canAssignForms: isConfigFlagTrue(cfg.can_assign_work_sample_forms),
    canCurate: isConfigFlagTrue(cfg.can_curate_work_samples),
    canRemove: isConfigFlagTrue(cfg.can_remove_work_samples),
    canSubmitPortfolios: isConfigFlagTrue(cfg.can_submit_portfolios),
    canReviewPortfolios: isConfigFlagTrue(cfg.can_review_portfolios),
    viewScope: normalizeWorkSamplesViewScope(cfg.work_samples_view_scope)
  };
}

export function canViewAllOrganizationsWorkSamples(caps) {
  return caps?.viewScope === WORK_SAMPLES_VIEW_SCOPES.ALL;
}

/**
 * Access copy for the Work Samples header — driven by page permission view_scope.
 * @param {{ viewScope?: string, orgName?: string|null, viewerVendors?: Array<{ vendor_name?: string|null }> }} opts
 * @returns {{ label: string, before: string, emphasis: string|null, after: string }}
 */
export function describeWorkSamplesAccess({ viewScope, orgName = null, viewerVendors = [] } = {}) {
  const scope = normalizeWorkSamplesViewScope(viewScope);
  const org = orgName ? String(orgName).trim() : '';
  const vendorNames = (viewerVendors || [])
    .map((v) => (v?.vendor_name ? String(v.vendor_name).trim() : ''))
    .filter(Boolean);
  const uniqueVendorNames = [...new Set(vendorNames)];

  if (scope === WORK_SAMPLES_VIEW_SCOPES.VENDOR) {
    if (uniqueVendorNames.length === 1) {
      return {
        label: 'Vendor',
        before: 'You are under the vendor group ',
        emphasis: uniqueVendorNames[0],
        after: '. You see students tied to this vendor.',
      };
    }
    if (uniqueVendorNames.length > 1) {
      return {
        label: 'Vendor',
        before: 'You are under the vendor groups ',
        emphasis: uniqueVendorNames.join(', '),
        after: '. You see students tied to these vendors.',
      };
    }
    return {
      label: 'Vendor',
      before: 'You are under a vendor group. You see students tied to your vendor.',
      emphasis: null,
      after: '',
    };
  }

  if (scope === WORK_SAMPLES_VIEW_SCOPES.ORGANIZATION) {
    if (org) {
      return {
        label: 'Organization',
        before: 'You see students across the whole organization (',
        emphasis: org,
        after: ').',
      };
    }
    return {
      label: 'Organization',
      before: 'You see students across the whole organization.',
      emphasis: null,
      after: '',
    };
  }

  if (scope === WORK_SAMPLES_VIEW_SCOPES.ALL) {
    if (org) {
      return {
        label: 'All organizations',
        before: 'Cross-organization access. Currently viewing ',
        emphasis: org,
        after: '.',
      };
    }
    return {
      label: 'All organizations',
      before: 'Cross-organization access. Select an organization to view its students.',
      emphasis: null,
      after: '',
    };
  }

  if (org) {
    return {
      label: 'Linked students',
      before: 'You see students who have you as Teacher of Record in ',
      emphasis: org,
      after: '.',
    };
  }
  return {
    label: 'Linked students',
    before: 'You see students who have you as Teacher of Record.',
    emphasis: null,
    after: '',
  };
}

export function formatLpTimingLabel(status) {
  if (status === 'current') return 'In progress';
  if (status === 'past') return 'Ended';
  if (status === 'upcoming') return 'Upcoming';
  return 'Unknown dates';
}

export const LP_TIMING_LEGEND = Object.freeze([
  {
    status: 'current',
    dotClass: 'ws-lp-dot--current',
    label: 'In progress',
    description: 'Today falls within this learning period\'s start and end dates.',
  },
  {
    status: 'past',
    dotClass: 'ws-lp-dot--past',
    label: 'Ended',
    description: 'This learning period has already closed.',
  },
  {
    status: 'upcoming',
    dotClass: 'ws-lp-dot--upcoming',
    label: 'Upcoming',
    description: 'This learning period has not started yet.',
  },
  {
    status: 'unknown',
    dotClass: 'ws-lp-dot--unknown',
    label: 'Unknown',
    description: 'Start or end dates are missing for this period.',
  },
]);

export function getLpTimingMeta(status) {
  return LP_TIMING_LEGEND.find((item) => item.status === status) || LP_TIMING_LEGEND[3];
}

const DEV_AS_OF_STORAGE_KEY = 'work_samples_dev_as_of_date';

export function isWorkSamplesDevToolsEnabled() {
  return process.env.NODE_ENV === 'development';
}

export function readDevAsOfDate() {
  if (!isWorkSamplesDevToolsEnabled()) return '';
  try {
    const v = localStorage.getItem(DEV_AS_OF_STORAGE_KEY) || '';
    return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '';
  } catch {
    return '';
  }
}

export function writeDevAsOfDate(isoDate) {
  if (!isWorkSamplesDevToolsEnabled()) return;
  try {
    if (isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
      localStorage.setItem(DEV_AS_OF_STORAGE_KEY, isoDate);
    } else {
      localStorage.removeItem(DEV_AS_OF_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function appendAsOfDateQuery(url, asOfDate) {
  if (!asOfDate || !/^\d{4}-\d{2}-\d{2}$/.test(asOfDate)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}as_of_date=${encodeURIComponent(asOfDate)}`;
}

export function formatDevAsOfLabel(isoDate) {
  if (!isoDate) return '';
  const dt = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return isoDate;
  return dt.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const PORTFOLIO_STATUS_LABELS = Object.freeze({
  draft: 'Draft',
  submitted: 'Submitted',
  in_review: 'In review',
  flagged: 'Flagged',
  flagged_submission: 'Flagged submission',
  passed: 'Passed',
});

/** Stored lifecycle status for display (never overridden by open flags). */
export function normalizeLifecycleStatus(storedStatus) {
  const status = storedStatus ? String(storedStatus).trim().toLowerCase() : null;
  if (!status || status === 'flagged') return status === 'flagged' ? 'submitted' : status;
  return PORTFOLIO_STATUS_LABELS[status] ? status : storedStatus;
}

export function formatPortfolioStatus(status) {
  if (!status) return 'No portfolio';
  const key = String(status).trim().toLowerCase();
  return PORTFOLIO_STATUS_LABELS[key] || status;
}

export function portfolioStatusClass(status) {
  if (!status) return 'ws-portfolio-status--none';
  const key = String(status).trim().toLowerCase();
  if (key === 'flagged_submission') return 'ws-portfolio-status--flagged';
  return `ws-portfolio-status--${key}`;
}

/** @deprecated Use normalizeLifecycleStatus — lifecycle is separate from open flags. */
export function resolveDisplayPortfolioStatus(storedStatus, openWorkSampleFlagCount = 0) {
  return normalizeLifecycleStatus(storedStatus);
}

export function formatAttentionLabel(openFlagCount = 0) {
  const n = Number(openFlagCount) || 0;
  if (n <= 0) return null;
  return n === 1 ? '1 open flag' : `${n} open flags`;
}

export function portfolioIsStructurallyLocked(lifecycleStatus) {
  const key = normalizeLifecycleStatus(lifecycleStatus);
  return key === 'submitted' || key === 'in_review' || key === 'passed';
}

export function canCuratePortfolioEvidence({
  lifecycleStatus,
  openFlagCount = 0,
  canCurate = false,
} = {}) {
  if (!canCurate) return false;
  const status = normalizeLifecycleStatus(lifecycleStatus);
  if (!status || status === 'draft') return true;
  if (status === 'passed') return false;
  return (Number(openFlagCount) || 0) > 0;
}

export function formatIssuesSummary(student) {
  const parts = [];
  const lpCount = student?.lps_needing_attention || 0;
  const flagCount = student?.open_flag_count || 0;
  if (student?.has_track === false) {
    if (lpCount === 0 && flagCount === 0) {
      return 'No track assigned';
    }
  }
  if (lpCount > 0) {
    parts.push(`${lpCount} LP${lpCount === 1 ? '' : 's'} need attention`);
  }
  if (flagCount > 0) {
    parts.push(`${flagCount} open flag${flagCount === 1 ? '' : 's'}`);
  }
  return parts.length ? parts.join(' · ') : '—';
}

export function formatLpProgress(cell) {
  const forms = cell?.form_count ?? cell?.assignment_count ?? 0;
  const withEvidence = cell?.forms_with_evidence_count ?? 0;
  if (!forms) return 'No forms yet';
  return `${withEvidence}/${forms} form${forms === 1 ? '' : 's'} with evidence`;
}

export function formatLpDateRange(start, end) {
  const fmt = (d) => {
    if (!d) return '—';
    const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
    return Number.isNaN(dt.getTime())
      ? String(d)
      : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  return `${fmt(start)} – ${fmt(end)}`;
}

/** Parse scheduler period date to local calendar day. */
export function parseLpLocalDate(value) {
  if (value == null || value === '') return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatLpShortDate(value) {
  const dt = parseLpLocalDate(value);
  if (!dt) return '';
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function computeLpTimelineFlexWeights(periods) {
  if (!periods?.length) return [];
  let min = null;
  let max = null;
  for (const lp of periods) {
    const start = parseLpLocalDate(lp.start_date);
    const end = parseLpLocalDate(lp.end_date) || start;
    if (start) min = min == null ? start.getTime() : Math.min(min, start.getTime());
    if (end) max = max == null ? end.getTime() : Math.max(max, end.getTime());
  }
  if (min == null || max == null) return periods.map(() => 1);
  const span = Math.max(max - min, 1);
  const minWeight = span * 0.07;
  return periods.map((lp) => {
    const start = parseLpLocalDate(lp.start_date)?.getTime() ?? min;
    const end = parseLpLocalDate(lp.end_date)?.getTime() ?? start;
    return Math.max(end - start, minWeight);
  });
}

/** 0–1 position of reference day within LP bounds (for timeline "today" tick). */
export function lpTodayProgress(lp, referenceIso = '') {
  if (lp?.timing_status !== 'current') return null;
  const start = parseLpLocalDate(lp.start_date);
  const end = parseLpLocalDate(lp.end_date);
  if (!start || !end || end <= start) return 0.5;
  const ref = referenceIso ? parseLpLocalDate(referenceIso) : new Date();
  if (!ref) return 0.5;
  const refMid = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime();
  const pct = (refMid - start.getTime()) / (end.getTime() - start.getTime());
  return Math.max(0, Math.min(1, pct));
}

export const WS_TIMELINE_LEGEND = Object.freeze([
  {
    key: 'past',
    className: 'ws-legend-timeline-bar ws-legend-timeline-bar--past',
    label: 'Past',
    description: 'Earlier learning periods on the track (muted bar).',
  },
  {
    key: 'current',
    className: 'ws-legend-timeline-bar ws-legend-timeline-bar--current',
    label: 'In progress',
    description: 'The filled segment where today falls; the tick marks today\'s position.',
  },
  {
    key: 'upcoming',
    className: 'ws-legend-timeline-bar ws-legend-timeline-bar--upcoming',
    label: 'Upcoming',
    description: 'Future learning periods (light bar).',
  },
  {
    key: 'selected',
    className: 'ws-legend-timeline-bar ws-legend-timeline-bar--selected',
    label: 'Selected',
    description: 'Click a segment to scope the roster table to that learning period\'s portfolio.',
  },
]);

export { buildUserProfileUrl } from './userProfileUrl';

/** Evidence mode allows multiple submissions per form in the portfolio. */
export function portfolioFormAllowsMultipleEvidence(evidenceMode) {
  return String(evidenceMode || '').trim().toLowerCase() === 'each_submission_in_lp';
}

/**
 * Compute next form_submission_ids after include/remove in the portfolio explorer.
 */
export function buildNextPortfolioSubmissionIds(currentIds, form, submissionId, action) {
  const current = (currentIds || []).map(String);
  const subId = String(submissionId);
  const formSubmissionIds = (form?.student_submissions || []).map((s) => String(s.id));

  if (action === 'remove') {
    return current.filter((id) => id !== subId);
  }

  if (portfolioFormAllowsMultipleEvidence(form?.evidence_mode)) {
    return current.includes(subId) ? current : [...current, subId];
  }

  const withoutFormSubs = current.filter((id) => !formSubmissionIds.includes(id));
  return [...withoutFormSubs, subId];
}
